import React, { useEffect } from "react";
import { Logo } from "@orcmatic/ui";

const HUB_URL = "https://orcmatic-hub.pages.dev";

const INK = "#04121d";
const TEAL = "#22d3ee";

type Service = {
  icon: string;
  title: string;
  text: string;
};

const services: Service[] = [
  {
    icon: "🧩",
    title: "Individuelle Software",
    text: "Maßgeschneiderte Anwendungen, die exakt zu Ihren Abläufen passen — von der Idee bis zum Betrieb.",
  },
  {
    icon: "☁️",
    title: "Fertige SaaS-Lösungen",
    text: "Sofort einsatzbereite Cloud-Produkte wie Zeiterfassung, Projekte und Lager — ohne Einrichtungsaufwand.",
  },
  {
    icon: "📊",
    title: "Excel-Schulungen",
    text: "Praxisnahe Trainings, die Ihr Team sicherer und produktiver im Umgang mit Excel machen.",
  },
  {
    icon: "🤖",
    title: "KI-Beratung",
    text: "Wir zeigen, wo künstliche Intelligenz in Ihrem Unternehmen echten, messbaren Mehrwert schafft.",
  },
];

const styles = {
  root: {
    margin: 0,
    padding: 0,
    minHeight: "100vh",
    background: INK,
    color: "#e6f4f7",
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    display: "flex",
    flexDirection: "column" as const,
    backgroundImage:
      "radial-gradient(1200px 600px at 75% -10%, rgba(34,211,238,0.18) 0%, rgba(8,145,178,0) 60%), radial-gradient(900px 500px at 10% 10%, rgba(8,145,178,0.12) 0%, rgba(8,145,178,0) 55%)",
  },
  header: {
    padding: "1.25rem 2rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: "1160px",
    width: "100%",
    margin: "0 auto",
  },
  headerLink: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#e6f4f7",
    textDecoration: "none",
    border: "1px solid rgba(230,244,247,0.18)",
    borderRadius: "100px",
    padding: "0.5rem 1.15rem",
  },
  hero: {
    width: "100%",
    maxWidth: "880px",
    margin: "0 auto",
    textAlign: "center" as const,
    padding: "4rem 1.5rem 2rem",
  },
  badge: {
    display: "inline-block",
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase" as const,
    color: TEAL,
    background: "rgba(34,211,238,0.1)",
    border: "1px solid rgba(34,211,238,0.28)",
    borderRadius: "100px",
    padding: "0.35rem 0.9rem",
    marginBottom: "1.75rem",
  },
  headline: {
    fontSize: "clamp(2.3rem, 6vw, 4.1rem)",
    fontWeight: 800,
    lineHeight: 1.08,
    letterSpacing: "-0.03em",
    margin: "0 0 1.35rem 0",
  },
  accent: {
    background: "linear-gradient(120deg, #67e3f4, #0891b2)",
    WebkitBackgroundClip: "text",
    backgroundClip: "text" as const,
    WebkitTextFillColor: "transparent",
    color: "transparent",
  },
  tagline: {
    fontSize: "clamp(1.02rem, 2.4vw, 1.25rem)",
    color: "rgba(230,244,247,0.62)",
    maxWidth: "620px",
    lineHeight: 1.6,
    margin: "0 auto 2.5rem",
  },
  ctaRow: {
    display: "flex",
    gap: "0.85rem",
    justifyContent: "center",
    flexWrap: "wrap" as const,
  },
  ctaPrimary: {
    display: "inline-block",
    background: "linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)",
    color: INK,
    fontSize: "1rem",
    fontWeight: 700,
    padding: "0.9rem 2.1rem",
    borderRadius: "12px",
    textDecoration: "none",
    boxShadow: "0 8px 32px rgba(8,145,178,0.45)",
  },
  ctaSecondary: {
    display: "inline-block",
    background: "rgba(230,244,247,0.04)",
    color: "#e6f4f7",
    fontSize: "1rem",
    fontWeight: 600,
    padding: "0.9rem 1.85rem",
    borderRadius: "12px",
    textDecoration: "none",
    border: "1px solid rgba(230,244,247,0.16)",
  },
  servicesSection: {
    width: "100%",
    maxWidth: "1160px",
    margin: "0 auto",
    padding: "3.5rem 1.5rem 1rem",
  },
  sectionEyebrow: {
    color: TEAL,
    fontSize: "0.8rem",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase" as const,
    textAlign: "center" as const,
    margin: "0 0 0.6rem",
  },
  sectionTitle: {
    fontSize: "clamp(1.7rem, 4vw, 2.4rem)",
    fontWeight: 800,
    letterSpacing: "-0.02em",
    textAlign: "center" as const,
    margin: "0 0 2.5rem",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "1.15rem",
  },
  card: {
    background: "rgba(255,255,255,0.035)",
    border: "1px solid rgba(230,244,247,0.1)",
    borderRadius: "16px",
    padding: "1.75rem",
    transition: "transform 0.18s, box-shadow 0.18s, border-color 0.18s",
  },
  cardIcon: {
    width: "48px",
    height: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.5rem",
    borderRadius: "12px",
    background: "linear-gradient(135deg, rgba(34,211,238,0.22), rgba(8,145,178,0.22))",
    border: "1px solid rgba(34,211,238,0.25)",
    marginBottom: "1.1rem",
  },
  cardTitle: {
    fontSize: "1.15rem",
    fontWeight: 700,
    margin: "0 0 0.5rem",
    color: "#ffffff",
  },
  cardText: {
    fontSize: "0.95rem",
    lineHeight: 1.6,
    color: "rgba(230,244,247,0.6)",
    margin: 0,
  },
  footer: {
    marginTop: "auto",
    padding: "2.5rem 2rem",
    textAlign: "center" as const,
    fontSize: "0.85rem",
    color: "rgba(230,244,247,0.35)",
    borderTop: "1px solid rgba(230,244,247,0.07)",
  },
} as const;

export function App() {
  useEffect(() => {
    document.body.style.margin = "0";
    document.body.style.padding = "0";
    document.body.style.background = INK;
    document.documentElement.style.height = "100%";
    document.body.style.minHeight = "100%";
  }, []);

  return (
    <div style={styles.root}>
      <header style={styles.header}>
        <Logo size={30} tone="light" />
        <a href={HUB_URL} style={styles.headerLink}>
          Anmelden
        </a>
      </header>

      <main>
        <section style={styles.hero}>
          <div style={styles.badge}>Software · SaaS · KI</div>
          <h1 style={styles.headline}>
            Digitale Lösungen, die Ihr{" "}
            <span style={styles.accent}>Unternehmen voranbringen</span>
          </h1>
          <p style={styles.tagline}>
            Orcmatic entwickelt individuelle Software, liefert fertige SaaS-Produkte
            und begleitet Sie bei Excel und künstlicher Intelligenz — alles aus einer Hand.
          </p>
          <div style={styles.ctaRow}>
            <a href={HUB_URL} style={styles.ctaPrimary}>
              App öffnen
            </a>
            <a href="#leistungen" style={styles.ctaSecondary}>
              Leistungen ansehen
            </a>
          </div>
        </section>

        <section id="leistungen" style={styles.servicesSection}>
          <p style={styles.sectionEyebrow}>Unsere Leistungen</p>
          <h2 style={styles.sectionTitle}>Was wir für Sie tun</h2>
          <div style={styles.grid}>
            {services.map((s) => (
              <article
                key={s.title}
                style={styles.card}
                onMouseEnter={(e) => {
                  const el = e.currentTarget;
                  el.style.transform = "translateY(-4px)";
                  el.style.boxShadow = "0 18px 40px -12px rgba(8,145,178,0.4)";
                  el.style.borderColor = "rgba(34,211,238,0.35)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget;
                  el.style.transform = "translateY(0)";
                  el.style.boxShadow = "none";
                  el.style.borderColor = "rgba(230,244,247,0.1)";
                }}
              >
                <div style={styles.cardIcon}>{s.icon}</div>
                <h3 style={styles.cardTitle}>{s.title}</h3>
                <p style={styles.cardText}>{s.text}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <footer style={styles.footer}>
        © {new Date().getFullYear()} Orcmatic. Alle Rechte vorbehalten.
      </footer>
    </div>
  );
}
